import { useReactiveVar } from '@apollo/client';

import { useMyCareerCenterInfoQuery } from 'core/graphql';
import { authState } from 'core/state';

import { ICareerCenter, transformCareerCenter } from './transform';
import { isCareerViewer } from './isCareerViewer';

interface IUseMyCareerCenter {
  /** `null` = not enrolled. This IS the membership gate (spec 0018 §3.1). */
  center: ICareerCenter | null;
  isMember: boolean;
  /**
   * Методист смотрит центр глазами студента (ТЗ 0039 доводка): раздел открыт,
   * но личных данных нет и записи не делаются. Отдельно от `isMember`
   * намеренно — смешать их значит однажды показать смотрящему кнопку,
   * которую сервер всё равно отобьёт.
   */
  isPreview: boolean;
  loading: boolean;
  error: string;
  refetch: () => void;
}

/**
 * The student's Career Center membership + overall progress.
 *
 * Skipped for anyone outside {Student, Methodist} (and for the demo user):
 * карьерные резолверы отбивают чужой LMS-токен, и у преподавателя это дало бы
 * пачку молчаливых PermissionDenied на каждой странице — хук смонтирован
 * в шапке приложения ради пункта меню.
 *
 * `isMember` is false while loading, so nothing career-related flashes before
 * membership is known.
 *
 * `cache-and-network` mirrors usePendingSurveysCount: a student enrolled by
 * staff mid-session sees the section appear on the next mount without a reload.
 */
export const useMyCareerCenter = ({
  skip: skipRequested = false,
}: { skip?: boolean } = {}): IUseMyCareerCenter => {
  const { active_role, isDemoUser } = useReactiveVar(authState);

  // ТЗ 0074: вызывающий может выключить запрос — шапка делает это у
  // приостановленного студента, которому сервер центр не отдаёт.
  const skip = skipRequested || !isCareerViewer(active_role) || Boolean(isDemoUser);

  const { data, loading, error, refetch } = useMyCareerCenterInfoQuery({
    fetchPolicy: 'cache-and-network',
    skip,
  });

  const center = skip ? null : transformCareerCenter(data);

  return {
    center,
    // Участник — только настоящий: предпросмотр членством не считается,
    // иначе методист попал бы во все ветки, писанные под студента.
    isMember: center !== null && !center.isPreview,
    isPreview: center?.isPreview ?? false,
    loading,
    error: error?.message || '',
    refetch,
  };
};
